import React, { useEffect, useState } from "react";
import { adminApi } from "./api";
import { Download, IndianRupee, ShoppingBag, TrendingUp, Boxes, AlertTriangle } from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
  LineChart, Line, Legend,
} from "recharts";

const PERIODS = [
  { value: "daily", label: "Today" },
  { value: "weekly", label: "Last 7 days" },
  { value: "monthly", label: "This month" },
  { value: "yearly", label: "This year" },
];

function toCSV(rows, headers) {
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const lines = [headers.map(esc).join(",")];
  rows.forEach((r) => lines.push(headers.map((h) => esc(r[h])).join(",")));
  return lines.join("\n");
}
function downloadCSV(name, csv) {
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = name; a.click();
  URL.revokeObjectURL(url);
}

const KPI = ({ icon: Icon, label, value, sub, accent = "#D4AF37" }) => (
  <div className="bg-ink-900 border border-white/5 p-6 space-y-3" data-testid={`report-kpi-${label.toLowerCase().replace(/\s+/g, "-")}`}>
    <div className="flex items-center justify-between">
      <p className="label-eyebrow text-mist">{label}</p>
      <Icon className="w-4 h-4 stroke-[1.5]" style={{ color: accent }} />
    </div>
    <p className="num-display text-3xl text-bone">{value}</p>
    {sub && <p className="text-xs text-mist font-light">{sub}</p>}
  </div>
);

export default function AdminReports() {
  const [period, setPeriod] = useState("monthly");
  const [sales, setSales] = useState(null);
  const [inv, setInv] = useState(null);
  const [charts, setCharts] = useState(null);
  const [orders, setOrders] = useState([]);

  const loadSales = async (p) => {
    const { data } = await adminApi.get("/reports/sales", { params: { period: p } });
    setSales(data);
  };

  useEffect(() => {
    Promise.all([
      adminApi.get("/reports/inventory"),
      adminApi.get("/dashboard/charts"),
      adminApi.get("/orders", { params: { limit: 500 } }),
    ]).then(([i, c, o]) => {
      setInv(i.data);
      setCharts(c.data);
      setOrders(o.data.orders);
    });
  }, []);

  useEffect(() => { loadSales(period); }, [period]);

  const exportInventory = () => {
    if (!inv) return;
    const csv = toCSV(inv.products.map((p) => ({
      sku: p.sku, name: p.name, variant: p.variant, mrp: p.mrp, selling_price: p.selling_price,
      stock_quantity: p.stock_quantity, low_stock_threshold: p.low_stock_threshold, status: p.status,
    })), ["sku", "name", "variant", "mrp", "selling_price", "stock_quantity", "low_stock_threshold", "status"]);
    downloadCSV(`inventory-${new Date().toISOString().slice(0, 10)}.csv`, csv);
  };

  const exportOrders = () => {
    const csv = toCSV(orders.map((o) => ({
      order_number: o.order_number,
      customer: o.customer_name,
      phone: o.phone || "",
      total: o.total,
      status: o.status,
      items: (o.items || []).map((i) => `${i.sku}x${i.qty}`).join("; "),
      created_at: o.created_at,
    })), ["order_number", "customer", "phone", "total", "status", "items", "created_at"]);
    downloadCSV(`orders-${new Date().toISOString().slice(0, 10)}.csv`, csv);
  };

  if (!sales || !inv || !charts) return <p className="text-mist">Loading…</p>;

  return (
    <div className="space-y-8" data-testid="admin-reports-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <p className="label-eyebrow text-gold">Business intelligence</p>
          <h1 className="font-serif text-4xl text-bone mt-1">Reports</h1>
        </div>
        <div className="flex flex-wrap gap-2">
          {PERIODS.map((p) => (
            <button
              key={p.value}
              onClick={() => setPeriod(p.value)}
              className={`label-eyebrow px-4 py-2.5 border transition-colors ${period === p.value ? "border-gold text-gold" : "border-white/10 text-mist hover:border-gold hover:text-gold"}`}
              data-testid={`report-period-${p.value}`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPI icon={IndianRupee} label="Revenue" value={`₹${Math.round(sales.revenue).toLocaleString("en-IN")}`} sub={PERIODS.find((p) => p.value === period)?.label} />
        <KPI icon={ShoppingBag} label="Orders" value={sales.order_count} accent="#3B82F6" />
        <KPI icon={TrendingUp} label="Avg order value" value={`₹${Math.round(sales.avg_order_value).toLocaleString("en-IN")}`} accent="#00D4C4" />
        <KPI icon={Boxes} label="Total SKUs" value={inv.total_skus} sub={`${inv.total_units.toLocaleString("en-IN")} units in stock`} accent="#A855F7" />
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-ink-900 border border-white/5 p-6">
          <div className="flex items-center justify-between mb-4">
            <p className="label-eyebrow text-gold">Revenue · 7-day trend</p>
          </div>
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={charts.revenue_7d}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
              <XAxis dataKey="date" stroke="#A39D98" fontSize={11} />
              <YAxis stroke="#A39D98" fontSize={11} />
              <Tooltip contentStyle={{ background: "#161515", border: "1px solid #D4AF37", color: "#F9F6F0" }} />
              <Legend wrapperStyle={{ fontSize: 11, color: "#A39D98" }} />
              <Line type="monotone" dataKey="revenue" stroke="#D4AF37" strokeWidth={2} />
              <Line type="monotone" dataKey="orders" stroke="#00D4C4" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-ink-900 border border-white/5 p-6">
          <p className="label-eyebrow text-gold mb-4">Inventory by warehouse</p>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={charts.inventory_by_warehouse}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
              <XAxis dataKey="warehouse" stroke="#A39D98" fontSize={11} />
              <YAxis stroke="#A39D98" fontSize={11} />
              <Tooltip contentStyle={{ background: "#161515", border: "1px solid #D4AF37", color: "#F9F6F0" }} />
              <Bar dataKey="units" fill="#3B82F6" radius={[2, 2, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Low stock detail */}
      <div className="bg-ink-900 border border-white/5 p-6" data-testid="report-low-stock">
        <div className="flex items-center justify-between mb-4">
          <p className="label-eyebrow text-rose-300 flex items-center gap-2">
            <AlertTriangle className="w-3.5 h-3.5" /> Low stock SKUs ({inv.low_stock_count})
          </p>
        </div>
        {inv.low_stock.length === 0 ? (
          <p className="text-mist font-light italic text-sm">All SKUs are above their thresholds. 🌿</p>
        ) : (
          <table className="w-full text-sm">
            <thead><tr className="text-left label-eyebrow text-mist border-b border-white/5">
              <th className="py-2">SKU</th><th>Name</th><th>Stock</th><th>Threshold</th>
            </tr></thead>
            <tbody>
              {inv.low_stock.map((p) => (
                <tr key={p.sku} className="border-b border-white/5 text-bone/90 font-light">
                  <td className="py-2 font-mono text-xs">{p.sku}</td>
                  <td>{p.name}</td>
                  <td className="text-rose-300">{p.stock_quantity}</td>
                  <td className="text-mist">{p.low_stock_threshold}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Exports */}
      <div className="bg-ink-900 border border-white/5 p-6">
        <p className="label-eyebrow text-gold mb-4">Data exports</p>
        <div className="flex gap-3 flex-wrap">
          <button onClick={exportInventory} className="btn-ghost !py-3" data-testid="export-inventory-btn">
            <Download className="w-3.5 h-3.5" /> Inventory CSV
          </button>
          <button onClick={exportOrders} className="btn-ghost !py-3" data-testid="export-orders-btn">
            <Download className="w-3.5 h-3.5" /> Orders CSV
          </button>
        </div>
        <p className="text-mist text-xs font-light mt-3">Exports use current data and are processed entirely in the browser.</p>
      </div>
    </div>
  );
}
